import type { Canvas, Object as FabricObject, GroupProps } from 'fabric';
import { Group, Rect, Textbox } from 'fabric';
import { CANVAS_DIMENSIONS } from '../constants';

const BANNER_PROTECTED_LABELS = new Set([
  'Banner CTA',
  'Banner CTA Title',
  'Banner CTA Subtitle',
  'Saiba mais',
]);

const BANNER_LOCK_PROPS = {
  lockMovementX: true,
  lockMovementY: true,
  lockRotation: true,
  lockScalingX: true,
  lockScalingY: true,
  hasControls: false,
} as const;

/**
 * Re-aplica as propriedades de proteção do Banner CTA em todos os objetos do
 * canvas após `loadFromJSON`. Necessário porque o Fabric.js v7 não inclui
 * `lockRotation`, `hasControls` etc. na serialização padrão.
 */
export function reapplyBannerProtections(canvas: Canvas): void {
  const protect = (obj: FabricObject) => {
    const label = (obj as unknown as { data?: { label?: string } }).data?.label ?? '';
    if (BANNER_PROTECTED_LABELS.has(label)) {
      obj.set(BANNER_LOCK_PROPS);
    }
    const children = (obj as unknown as { getObjects?: () => FabricObject[] }).getObjects?.();
    if (children) children.forEach(protect);
  };
  canvas.getObjects().forEach(protect);
}

/**
 * Constrói o grupo Fabric.js do banner CTA para o device informado.
 *
 * O grupo é posicionado no canto inferior direito do canvas de acordo com as
 * dimensões de `CANVAS_DIMENSIONS`. Todas as propriedades de bloqueio de
 * movimento e escala já estão aplicadas — o banner CTA não pode ser movido
 * nem redimensionado pelo usuário.
 *
 * @param device - `'desktop'` ou `'mobile'`
 * @returns Instância `Group` do Fabric.js pronta para ser adicionada ao canvas
 *
 * @example
 * ```ts
 * const group = buildBannerCtaGroup('desktop');
 * canvas.add(group);
 * ```
 */
export function buildBannerCtaGroup(device: 'desktop' | 'mobile'): Group {
  const isMobile = device === 'mobile';
  const W = isMobile ? CANVAS_DIMENSIONS.mobile.width : CANVAS_DIMENSIONS.desktop.width;
  const H = isMobile ? CANVAS_DIMENSIONS.mobile.height : CANVAS_DIMENSIONS.desktop.height;

  // Dimensões responsivas
  const bannerHeight = isMobile ? 52 : 68; // Aumentado ligeiramente para mobile
  const titleFontSize = isMobile ? 12 : 18;
  const subtitleFontSize = isMobile ? 9 : 12;
  const subtitleTop = isMobile ? 28 : 38;
  const bgWidth = isMobile ? W * 0.92 : W * 0.9; // Mais largo no mobile
  const btnWidth = isMobile ? W * 0.32 : W * 0.175; // Botão mais largo no mobile
  const btnHeight = bannerHeight * (isMobile ? 0.5 : 0.44);
  const btnFontSize = isMobile ? 9 : 11;
  const btnRightPadding = isMobile ? 8 : 10;
  const btnLeft = bgWidth - btnWidth - btnRightPadding;
  const btnTop = isMobile ? 14 : 19;
  const radius = isMobile ? 8 : 10;

  // Margens para melhor touch target no mobile
  const titleLeft = isMobile ? 14 : 20;
  const titleTop = isMobile ? 12 : 16;

  const bannerBg = new Rect({
    left: 0,
    top: 0,
    width: bgWidth,
    height: bannerHeight,
    fill: 'rgba(20,20,20,0.85)', // Mais opaco no mobile para melhor legibilidade
    stroke: '#525252',
    strokeWidth: 1,
    rx: radius,
    ry: radius,
    originX: 'left',
    originY: 'top',
    evented: false,
  });

  const title = new Textbox('Inscreva-se', {
    left: titleLeft,
    top: titleTop,
    originX: 'left',
    originY: 'top',
    fontSize: titleFontSize,
    fontWeight: '700',
    fill: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    selectable: true,
    evented: true,
    hasControls: false,
    hasBorders: true,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    width: isMobile ? 100 : undefined, // Largura fixa no mobile para evitar quebra
    data: { label: 'Banner CTA Title' },
  } as ConstructorParameters<typeof Textbox>[1] & Record<string, unknown>);

  const subtitle = new Textbox('Abertura: 00/00/0000', {
    left: titleLeft,
    top: subtitleTop,
    originX: 'left',
    originY: 'top',
    fontSize: subtitleFontSize,
    fill: 'rgba(255,255,255,0.72)',
    fontFamily: 'Inter, sans-serif',
    selectable: true,
    evented: true,
    hasControls: false,
    hasBorders: true,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    width: isMobile ? 110 : 400,
    data: { label: 'Banner CTA Subtitle' },
  } as ConstructorParameters<typeof Textbox>[1] & Record<string, unknown>);

  const btnBg = new Rect({
    width: btnWidth,
    height: btnHeight,
    fill: 'transparent',
    stroke: 'rgba(238,242,246,0.24)', // Mais visível no mobile
    strokeWidth: isMobile ? 1.2 : 1,
    rx: 7,
    ry: 7,
    selectable: true,
    evented: true,
  });

  const btnText = new Textbox('Saiba mais ↗', {
    fontSize: btnFontSize,
    fill: '#ffffff',
    fontWeight: '600',
    fontFamily: 'Inter, sans-serif',
    width: btnWidth,
    textAlign: 'center',
    originX: 'center',
    originY: 'center',
    selectable: true,
    evented: true,
    hasControls: false,
    hasBorders: true,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
  });

  const btn = new Group([btnBg, btnText], {
    left: btnLeft,
    top: btnTop,
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    hasControls: false,
    hasBorders: true,
    subTargetCheck: true,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    data: {
      id: crypto.randomUUID(),
      label: 'Saiba mais',
      href: '',
    },
  } as Partial<GroupProps> & Record<string, unknown>);

  return new Group([bannerBg, title, subtitle, btn], {
    left: W * 0.5,
    top: H - bannerHeight,
    interactive: true,
    subTargetCheck: true,
    selectable: true,
    hasControls: false,
    hasBorders: true,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    data: {
      id: crypto.randomUUID(),
      label: 'Banner CTA',
    },
  } as Partial<GroupProps> & Record<string, unknown>);
}
